import React, { useEffect, useState } from 'react';
import { getEnterpriseAnalytics } from '../services/api';
import './AdminPages.css';

const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getEnterpriseAnalytics(days)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load analytics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [days]);

  const growth = data?.growth || [];
  const maxGrowth = Math.max(...growth.map((g) => g.total), 1);
  const labelEvery = days > 45 ? 14 : days > 14 ? 5 : 1;
  const risk = data?.risk || {};
  const sla = data?.sla || {};
  const people = data?.users || {};
  const access = data?.access || {};

  return (
    <div className="admin-page analytics-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-lead">Requests, people, and access that needs attention in the selected period.</p>
        </div>
        <select className="form-select analytics-range" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Time range">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      {loading && <p className="page-lead">Loading analytics…</p>}

      {data && (
        <>
          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-label">Opened</div>
              <div className="stat-value">{sla.createdCount ?? 0}</div>
              <div className="stat-sub">{sla.approvedCount || 0} approved · {sla.rejectedCount || 0} rejected</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Waiting now</div>
              <div className="stat-value">{sla.pendingCount ?? 0}</div>
              <div className="stat-sub">Median decision {sla.medianHours ?? '—'} h</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">People</div>
              <div className="stat-value">{people.total ?? 0}</div>
              <div className="stat-sub">{people.active || 0} active · {people.newInPeriod || 0} new</div>
            </div>
            <div className={`stat-card risk-${risk.riskLevel || 'low'}`}>
              <div className="stat-label">Needs attention</div>
              <div className="stat-value">{risk.score ?? 0}</div>
              <div className="stat-sub">{people.missingGithubUsername || 0} missing GitHub username · {access.expiringSoon || 0} expiring</div>
            </div>
          </div>

          <div className="card">
            <h3>Requests opened</h3>
            <p className="analytics-note">Each bar is one day. Taller means more requests were filed.</p>
            <div className="analytics-legend">
              <span><i className="swatch swatch-total" /> Opened</span>
            </div>
            <div className="analytics-chart">
              {growth.map((g, index) => (
                <div key={g.date} className="analytics-col" title={`${g.date}: ${g.total} opened, ${g.approved} approved, ${g.rejected} rejected`}>
                  <div className="analytics-bar" style={{ height: `${Math.max(g.total ? 4 : 0, (g.total / maxGrowth) * 100)}%` }} />
                  {index % labelEvery === 0 && <span className="analytics-tick">{g.date.slice(5)}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="analytics-split">
            <div className="card">
              <h3>Repositories</h3>
              {(data.topRepos || []).length === 0 ? <p className="analytics-note">No repository requests in this period.</p> : (
                <table className="simple-table">
                  <thead>
                    <tr><th>Repository</th><th>Requests</th></tr>
                  </thead>
                  <tbody>
                    {data.topRepos.map((r) => (
                      <tr key={r.repoId || r.fullName}>
                        <td>{r.fullName}</td>
                        <td>{r.requestCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card">
              <h3>People requested</h3>
              {(data.topPeople || []).length === 0 ? <p className="analytics-note">No named people in this period.</p> : (
                <table className="simple-table">
                  <thead>
                    <tr><th>GitHub username</th><th>Requests</th></tr>
                  </thead>
                  <tbody>
                    {data.topPeople.map((person) => (
                      <tr key={person.githubUsername}>
                        <td>{person.githubUsername}</td>
                        <td>{person.requestCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="analytics-split">
            <div className="card">
              <h3>Accounts</h3>
              <ul className="analytics-facts">
                <li><span>Admins</span><strong>{people.admins || 0}</strong></li>
                <li><span>Managers</span><strong>{people.managers || 0}</strong></li>
                <li><span>MFA on</span><strong>{people.mfaEnabled || 0}</strong></li>
                <li><span>Password change required</span><strong>{people.mustChangePassword || 0}</strong></li>
                <li><span>No GitHub username</span><strong>{people.missingGithubUsername || 0}</strong></li>
              </ul>
            </div>
            <div className="card">
              <h3>Current access</h3>
              <ul className="analytics-facts">
                <li><span>Active grants</span><strong>{access.activeGrants || 0}</strong></li>
                <li><span>Expiring in 14 days</span><strong>{access.expiringSoon || 0}</strong></li>
                {(access.byPermission || []).map((row) => (
                  <li key={row.permission}><span>{row.permission}</span><strong>{row.count}</strong></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="card">
            <h3>What to look at</h3>
            {(risk.findings || []).length === 0 && <p className="analytics-note">Nothing unusual in this snapshot.</p>}
            <ul className="findings-list">
              {(risk.findings || []).map((f) => (
                <li key={`${f.type}-${f.detail}`} className={`finding finding--${f.severity}`}>
                  <strong>{f.type}</strong>: {f.detail}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
